import { permissionsSelector } from "modules/Login/selectors/permissionSelector";
import useDeactivateExpenseAccount from "../hook/useDeactivateExpenseAccount";
import useGetExpenseAccounts from "../hook/useGetExpenseAccounts";
import { PERMISSIONS } from "modules/PermissionsConstants";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
  addStateExpenseAccount,
  editStateExpenseAccount,
  expenseAccountFetching,
  expenseAccountFilterText,
  expenseAccountReFetching,
  rowEditExpenseAccount,
  selectedRowsStateExpenseAccount,
} from "../atoms/atoms";
import { IExpenseAccount, IExpenseAccountTableData } from "../type";
import { useEffect, useState } from "react";
import Timer from "components/Timer";
import { toast } from "components/toastNotification";
import { useLocation } from "react-router-dom";
import { notificationFilterById } from "actions/Utilities/NotificationFilter";
import EditButton from "components/EditButton";
import TableFilters from "components/TableFilters";
import AddButton from "components/AddButton";
import MaterialTableTS from "components/tables/MaterialTableTS";
import ConfirmBoxDelete from "components/ConfirmBoxDelete";
import ExpenseAccountForm from "../form/expenseAccountForm";

const ExpenseAccountTable = ({}) => {
  const { data: directData, refetch: reFetch } = useGetExpenseAccounts();
  const BulkDelete = useDeactivateExpenseAccount();

  // Atoms Call
  const [actionAddState, setActionAddState] = useRecoilState(
    addStateExpenseAccount
  );
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateExpenseAccount
  );
  const [actionEditState, setActionEditState] = useRecoilState(
    editStateExpenseAccount
  );
  const [filterTextState, setFilterTextState] = useRecoilState(
    expenseAccountFilterText
  );
  const setEditRowData = useSetRecoilState(rowEditExpenseAccount);
  const userPermissions = useRecoilValue(permissionsSelector);

  const [isFetching, setIsFetching] = useRecoilState(expenseAccountFetching);
  const isReFetching = useRecoilValue(expenseAccountReFetching);

  // Local State
  const [tableData, setTableData] = useState<IExpenseAccount[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [tableFilteredData, setTableFilteredData] = useState<IExpenseAccount[]>(
    []
  );

  // Permisision States
  const addPermission = PERMISSIONS.permission_utilities_expenseaccount_add;
  const editPermission = PERMISSIONS.permission_utilities_expenseaccount_edit;
  const deletePermission =
    PERMISSIONS.permission_utilities_expenseaccount_delete;

  // Get Data
  const Loader = async () => {
    setTableData(directData?.data.map((data) => ({ ...data })));
    await Timer();
    setIsFetching(false);
  };

  useEffect(() => {
    reFetch();
  }, [isReFetching]);

  useEffect(() => {
    if (directData?.status == 400) {
      toast.fire("Server Error", directData?.data.Message, "error");
      setTableData([]);
      setIsFetching(false);
    } else {
      Loader();
    }
  }, [directData]);

  // For Recent Activity + Search
  let location = useLocation();
  let recordIdFromRecent;

  const [recent, setRecent] = useState("");
  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      notificationFilterById(
        recordIdFromRecent.recordId,
        recordIdFromRecent.moduleName,
        recordIdFromRecent.subModuleName
      ).then((result: IExpenseAccount[] | undefined) => {
        if (typeof result !== "object") {
          toast.fire("Failed", result, "error");
        } else {
          setTableFilteredData(result);
          setRecent(result[0].expenseAccount);
        }
      });
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
    setSelectedRows([]);
  }, [recordIdFromRecent]);

  // For Filter/Search
  const filtereditems =
    tableData &&
    tableData?.filter(
      (item) =>
        item.expenseAccount &&
        item.expenseAccount
          .toLowerCase()
          .includes(filterTextState?.trim().toLowerCase())
    );

  useEffect(() => {
    if (!recent) setTableFilteredData(filtereditems);
  }, [filterTextState]);

  // Handle Functions
  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows: IExpenseAccountTableData[]) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });

    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData: IExpenseAccountTableData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = async () => {
    setIsFetching(true);

    const ids = selectedRows && selectedRows.map((row) => row.id);

    BulkDelete(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">
      {`Are you sure you want to deactivate ${rowsCount} ${
        rowsCount > 1 ? "Expense Accounts" : "Expense Account"
      }`}
    </div>
  );

  const matColumns = [
    {
      title: "Expense Account",
      field: "expenseAccount",
      customSort: (a, b) =>
        a.expenseAccount && b.expenseAccount
          ? a.expenseAccount.trim().localeCompare(b.expenseAccount.trim())
          : a.expenseAccount
          ? 0
          : -1,
    },
    {
      title: "Description",
      field: "description",
      customSort: (a, b) =>
        a.description && b.description
          ? a.description.trim().localeCompare(b.description.trim())
          : a.description
          ? 0
          : -1,
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData?.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      sorting: false,
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="bg-white p-4">
      {actionAddState || actionEditState ? (
        <div>
          <ExpenseAccountForm setRecent={setRecent} />
        </div>
      ) : (
        <div className="">
          <div className="flex w-full">
            <div className="bg-white w-full">
              <div className="flex justify-between text-xs items-center w-full">
                <div className="">
                  <TableFilters
                    defaultText={filterTextState}
                    textInputName={"expenseAccountName"}
                    columnDetails={"Expense Account Name"}
                    filterText={setFilterTextState}
                    recentText={recent}
                    setRecent={setRecent}
                    disabledButton={
                      actionEditState ||
                      (selectedRows !== undefined && selectedRows.length > 0)
                    }
                    disabledTextBox={
                      selectedRows !== undefined && selectedRows.length > 0
                    }
                  />
                </div>
                <div className="">
                  {userPermissions.includes(addPermission) ? (
                    <AddButton
                      buttonText="New Expense Account"
                      onClick={() => handleAdd()}
                      disabled={
                        actionEditState ||
                        (selectedRows !== undefined && selectedRows.length > 0)
                      }
                    />
                  ) : (
                    ""
                  )}
                </div>
              </div>

              <div>
                {userPermissions.includes(deletePermission) ? (
                  <div className="text-secondary italic text-xs3 my-2">
                    <p className="font-bold italic inline-block mr-1">Note:</p>
                    <span>Tick the box to deactivate some data.</span>
                  </div>
                ) : (
                  <div className="mb-4"></div>
                )}
              </div>

              <div className="">
                <MaterialTableTS
                  data={
                    isFetching
                      ? []
                      : filterTextState === "" && recent === ""
                      ? tableData
                      : filterTextState === ""
                      ? tableFilteredData
                      : filtereditems
                  }
                  showTitle={true}
                  columns={matColumns}
                  isLoading={isFetching}
                  onSelectionChange={handleSelectedRowsChange}
                  selectedRows={selectedRows}
                  selection={
                    !actionEditState &&
                    !actionAddState &&
                    userPermissions.includes(deletePermission)
                  }
                  setConfirmModalOpen={setOpenModalDelete}
                />
              </div>
            </div>
          </div>

          <ConfirmBoxDelete
            ModalTitle="Deactivate Expense Account"
            qConfirmation={deleteMessage}
            isOpen={openModalDelete}
            onClose={() => {
              setOpenModalDelete(false);
            }}
            onClickFunction={handleDelete}
            cancelButtonRef={undefined}
          />
        </div>
      )}
    </div>
  );
};

export default ExpenseAccountTable;
